import React from "react";
import { Box, Typography, Grid, Select, Option, Stack } from "@mui/joy";
import { Gauge, Hash, Plus, X } from "lucide-react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import {
    FormInput,
    FormActionSelect,
    Button,
    IconButton,
} from "../../../shared";

interface DynamicVentFieldProps {
    name: string;
    title: string;
    description: string;
    sizes: string[];
    disabled?: boolean;
}

function DynamicVentField({
    name,
    title,
    description,
    sizes,
    disabled,
}: DynamicVentFieldProps) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });

    return (
        <Box
            sx={{
                width: "100%",
                bgcolor: "background.surface",
                p: 1.5,
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <Typography
                level="body-sm"
                sx={{
                    textTransform: "uppercase",
                    fontSize: "12px",
                    fontWeight: "xl",
                    letterSpacing: "0.5px",
                    mb: 0.5,
                }}
            >
                {title}
            </Typography>
            <Typography
                level="body-xs"
                color="neutral"
                sx={{ mt: -0.5, mb: 1.5, fontSize: "12px" }}
            >
                {description}
            </Typography>

            <Stack spacing={1.5}>
                {fields.map((item, index) => (
                    <Box
                        key={item.id}
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 1, sm: 0 },
                            mb: { xs: 0.5, sm: 0 },
                            pb: { xs: 1.5, sm: 0 },
                            borderBottom: {
                                xs: "1px solid var(--standard-palette-divider)",
                                sm: "none",
                            },
                            width: "100%",
                            minWidth: 0,
                        }}
                    >
                        {/* Size + mobile delete */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                width: { xs: "100%", sm: 140 },
                                flexShrink: 0,
                                minWidth: 0,
                                alignItems: "stretch",
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    width: { sm: 140 },
                                }}
                            >
                                <Controller
                                    name={`${name}.${index}.Size`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            size="md"
                                            disabled={disabled}
                                            onChange={(_, newValue) =>
                                                field.onChange(newValue)
                                            }
                                            placeholder="Size"
                                            startDecorator={<Gauge size={16} />}
                                            sx={{
                                                bgcolor: "background.surface",
                                                borderRadius: "4px",
                                                boxShadow: "none",
                                                width: "100%",
                                                borderTopRightRadius: {
                                                    xs: "4px",
                                                    sm: 0,
                                                },
                                                borderBottomRightRadius: {
                                                    xs: "4px",
                                                    sm: 0,
                                                },
                                            }}
                                        >
                                            {sizes.map((size) => (
                                                <Option key={size} value={size}>
                                                    {size}
                                                </Option>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </Box>
                            <IconButton
                                variant="soft"
                                color="danger"
                                disabled={disabled}
                                onClick={() => remove(index)}
                                sx={{
                                    display: { xs: "flex", sm: "none" },
                                    height: "auto",
                                    alignSelf: "stretch",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <X size={16} />
                            </IconButton>
                        </Box>

                        {/* Qty + Action + desktop delete */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: { xs: 1, sm: 0 },
                                flex: 1,
                                width: "100%",
                                minWidth: 0,
                                alignItems: "flex-start",
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name={`${name}.${index}.Qty`}
                                    type="number"
                                    placeholder="Qty"
                                    disabled={disabled}
                                    startDecorator={<Hash size={16} />}
                                    sx={{
                                        borderRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                        borderLeft: { xs: undefined, sm: 0 },
                                        minWidth: 0,
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    width: { xs: "42%", sm: 100 },
                                    flexShrink: 0,
                                    minWidth: 0,
                                }}
                            >
                                <FormActionSelect
                                    name={`${name}.${index}.Action`}
                                    disabled={disabled}
                                    sx={{
                                        borderTopLeftRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                        borderBottomLeftRadius: {
                                            xs: "4px",
                                            sm: 0,
                                        },
                                        borderRadius: {
                                            xs: "4px",
                                            sm: undefined,
                                        },
                                    }}
                                />
                            </Box>

                            <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                disabled={disabled}
                                onClick={() => remove(index)}
                                sx={{
                                    display: { xs: "none", sm: "flex" },
                                    mt: 0.5,
                                    ml: 1,
                                }}
                            >
                                <X size={20} />
                            </IconButton>
                        </Box>
                    </Box>
                ))}

                <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Plus size={16} />}
                    onClick={() => append({ Size: "", Qty: "", Action: "" })}
                    disabled={disabled}
                    sx={{ alignSelf: "flex-start", mt: 1 }}
                >
                    Add {title}
                </Button>
            </Stack>
        </Box>
    );
}

export function Penetrations({ loading }: { loading?: boolean }) {
    const { control } = useFormContext();

    return (
        <Box>
            <Box mb={2}>
                <Typography level="title-md">Penetrations</Typography>
                <Typography level="body-sm" color="neutral">
                    Specify penetration types and components like bath, dryer,
                    and furnace vents.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Grid container spacing={2.5}>
                    {/* Bath Vent */}
                    <Grid xs={12} sm={12} md={6} lg={6}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                level="body-xs"
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: "xl",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Bath Vent
                            </Typography>
                            <Typography
                                level="body-xs"
                                color="neutral"
                                sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}
                            >
                                Size, quantity and action.
                            </Typography>
                            <Box
                                sx={{
                                    width: "100%",
                                    overflowX: "auto",
                                    pb: 1,
                                    mb: -1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 0,
                                        alignItems: "flex-start",
                                        minWidth: "max-content",
                                    }}
                                >
                                    <Box sx={{ width: 100 }}>
                                        <Controller
                                            name="BathVentSize"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    size="md"
                                                    disabled={loading}
                                                    onChange={(_, newValue) =>
                                                        field.onChange(newValue)
                                                    }
                                                    placeholder="Size"
                                                    startDecorator={
                                                        <Gauge size={16} />
                                                    }
                                                    sx={{
                                                        bgcolor:
                                                            "background.surface",
                                                        borderRadius: "4px",
                                                        boxShadow: "none",
                                                        borderTopRightRadius: 0,
                                                        borderBottomRightRadius: 0,
                                                        borderRight: 0,
                                                    }}
                                                >
                                                    <Option value="4 inch">
                                                        4 inch
                                                    </Option>
                                                    <Option value="5 inch">
                                                        5 inch
                                                    </Option>
                                                </Select>
                                            )}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            width: { xs: 80, md: "auto" },
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <FormInput
                                            name="BathVentQty"
                                            type="number"
                                            placeholder="Qty"
                                            disabled={loading}
                                            startDecorator={<Hash size={16} />}
                                            sx={{
                                                borderRadius: 0,
                                                borderLeft: 0,
                                                borderRight: 0,
                                                minWidth: 0,
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ width: 100, flexShrink: 0 }}>
                                        <FormActionSelect
                                            name="BathVentAction"
                                            disabled={loading}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Dryer Vent */}
                    <Grid xs={12} sm={12} md={6} lg={6}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                level="body-xs"
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: "xl",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Dryer Vent
                            </Typography>
                            <Typography
                                level="body-xs"
                                color="neutral"
                                sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}
                            >
                                Size, quantity and action.
                            </Typography>
                            <Box
                                sx={{
                                    width: "100%",
                                    overflowX: "auto",
                                    pb: 1,
                                    mb: -1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 0,
                                        alignItems: "flex-start",
                                        minWidth: "max-content",
                                    }}
                                >
                                    <Box sx={{ width: 90 }}>
                                        <Controller
                                            name="DryerVentSize"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    size="md"
                                                    disabled={loading}
                                                    onChange={(_, newValue) =>
                                                        field.onChange(newValue)
                                                    }
                                                    placeholder="Size"
                                                    startDecorator={
                                                        <Gauge size={16} />
                                                    }
                                                    sx={{
                                                        bgcolor:
                                                            "background.surface",
                                                        borderRadius: "4px",
                                                        boxShadow: "none",
                                                        borderTopRightRadius: 0,
                                                        borderBottomRightRadius: 0,
                                                        borderRight: 0,
                                                    }}
                                                >
                                                    <Option value="4 inch">
                                                        4 inch
                                                    </Option>
                                                    <Option value="5 inch">
                                                        5 inch
                                                    </Option>
                                                </Select>
                                            )}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            width: { xs: 80, md: "auto" },
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <FormInput
                                            name="DryerVentQty"
                                            type="number"
                                            placeholder="Qty"
                                            disabled={loading}
                                            startDecorator={<Hash size={16} />}
                                            sx={{
                                                borderRadius: 0,
                                                borderLeft: 0,
                                                borderRight: 0,
                                                minWidth: 0,
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ width: 100, flexShrink: 0 }}>
                                        <FormActionSelect
                                            name="DryerVentAction"
                                            disabled={loading}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Furnace Vents (Dynamic) */}
                    <Grid xs={12} sm={12} md={12} lg={12}>
                        <DynamicVentField
                            name="FurnaceVents"
                            title="Furnace Vent"
                            description="Add one or more furnace vents, specifying size and quantity."
                            sizes={[
                                "4 inch",
                                "5 inch",
                                "6 inch",
                                "7 inch",
                                "TBD",
                            ]}
                            disabled={loading}
                        />
                    </Grid>

                    {/* Plumbing Jacks */}
                    <Grid xs={12} sm={12} md={6} lg={6}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                level="body-xs"
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: "xl",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Plumbing Jack Count
                            </Typography>
                            <Typography
                                level="body-xs"
                                color="neutral"
                                sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}
                            >
                                Number of plumbing jacks and action.
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 0,
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <FormInput
                                        name="PlumbingJacksQty"
                                        type="number"
                                        placeholder="0"
                                        sx={{
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                            borderRight: 0,
                                            minWidth: 0,
                                        }}
                                        disabled={loading}
                                        startDecorator={<Hash size={16} />}
                                    />
                                </Box>
                                <Box sx={{ width: 100, flexShrink: 0 }}>
                                    <FormActionSelect
                                        name="PlumbingJacksAction"
                                        disabled={loading}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Split Boots */}
                    <Grid xs={12} sm={12} md={6} lg={6}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                level="body-xs"
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: "xl",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Split Boot Count
                            </Typography>
                            <Typography
                                level="body-xs"
                                color="neutral"
                                sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}
                            >
                                Number of split boots and action.
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 0,
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <FormInput
                                        name="SplitBootsQty"
                                        type="number"
                                        placeholder="0"
                                        sx={{
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0,
                                            borderRight: 0,
                                            minWidth: 0,
                                        }}
                                        disabled={loading}
                                        startDecorator={<Hash size={16} />}
                                    />
                                </Box>
                                <Box sx={{ width: 100, flexShrink: 0 }}>
                                    <FormActionSelect
                                        name="SplitBootsAction"
                                        disabled={loading}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
